import { stringToPlan, runCheckoutSession } from "frontend/billingUtils";
import { Action, DispatchFunc } from "frontend/state/actions";
import { deleteCookie, readCookieValue, setCookie } from "frontend/utils/cookie-utils";
import consts, { Plan } from "shared/consts";
import { dateDiffInDays } from "shared/util/billing";
import { checkAuthStatus, createFreePlan, getUser } from "frontend/api";
import tracking from "../tracking";
import { getUserIdFromMondayContext } from "./monday-api-service";
import { getIframeSessionToken, isCIM } from "utils/url-utils";
import { getInitialAuthStatus, getInitialUser } from "utils/server-props-cache";

export async function initUserData(dispatch: DispatchFunc) {
  try {
    const authCheckStatus = getInitialAuthStatus();

    const { authenticated, blocked, token } = authCheckStatus?.authenticated
      ? authCheckStatus
      : await checkAuthStatus(getIframeSessionToken(), {
          shouldReturnToken: true,
        });

    if (token) {
      let hostname = window.location.hostname;
      const newHostname = window.location.hostname.split(".").slice(1).join(".");
      hostname = newHostname.length > 0 ? newHostname : hostname;
      setCookie(consts.CANVAS_AUTH_TOKEN, token, hostname);
    }

    if (blocked) {
      throw new Error("blocked");
    }

    if (!authenticated) {
      throw new Error("User not authenticated");
    }
    let user = getInitialUser() ?? (await getUser());
    if (!user) {
      const selectedPlanId = readCookieValue(consts.COOKIE_NAMES.CANVAS_PLAN_ID);
      if (selectedPlanId && (stringToPlan(selectedPlanId) === Plan.pro || stringToPlan(selectedPlanId) === Plan.team)) {
        //if the user chose a priced package before signup, take him straight to checkout page
        deleteCookie(consts.COOKIE_NAMES.CANVAS_PLAN_ID, "/", ".workcanvas.com"); //if a user don't want to pay during checkout, let him go back to site with a free plan
        runCheckoutSession(stringToPlan(selectedPlanId)!);
        return;
      } else {
        //create free canvas plan
        user = await createFreePlan().then(() => getUser());
        updateUser(user);
      }
    }

    if (isCIM()) {
      const mondayContextUserId = await getUserIdFromMondayContext();
      if (mondayContextUserId && user?.id !== mondayContextUserId) {
        tracking.trackAnalyticsEvent("canvas_monday_wrong_user", {
          userId: user?.id,
          contextUserId: mondayContextUserId,
        });
        throw new Error("Authorization error: Different Monday user detected in the iframe");
      }
    }

    const userDailyValue = consts.PRICING.PRO.VALUE / 30;
    const now = new Date();
    const retentionDays = dateDiffInDays(user?.canvasCreatedDate ? new Date(user.canvasCreatedDate) : now, now);
    if (retentionDays >= 1) {
      const retentionValue = retentionDays * userDailyValue;
      tracking.reportGoogleAnalyticsConversion(consts.GA_CONVERSION_TYPES.RETURNING_USER, retentionValue);
    }
    updateUser(user);
  } catch (e) {
    if (e instanceof Error && e.message === "blocked") {
      dispatch({ type: Action.BlockedUser, payload: null });
      return;
    }
    dispatch({ type: Action.FailedLoadingUser, payload: null });
    throw e;
  }

  function updateUser(user: any) {
    dispatch({ type: Action.UpdatedUser, payload: user });
  }
}
